import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ListChecks, Pencil, Play, MoreHorizontal, History, Loader2, Power } from 'lucide-react';
import { WorkflowAppShell } from '../components/workflow/WorkflowAppShell';
import { WorkflowStepsTab } from '../components/workflow/WorkflowStepsTab';
import { WorkflowRunsTab } from '../components/workflow/WorkflowRunsTab';
import { WORKFLOW, INITIAL_STEPS, INITIAL_RUNS, INITIAL_INPUTS, DEFAULT_INPUT_VALUES, OUTPUT_URL, LIVE_STEP_DETAIL, LIVE_STEP_DURATION, LIVE_STEP_LOG, WorkflowStep, WorkflowInput, WorkflowRun, StepTrace } from '../components/workflow/workflowData';
type Tab = 'steps' | 'runs';
/**
 * Workflow detail mockup. Two tabs — Steps (read + edit in place) and Runs
 * (history with a per-step trace). "Run now" streams a live run into the Runs
 * tab so the reviewer can watch a workflow execute end to end.
 */
export function WorkflowShow() {
  const [tab, setTab] = useState<Tab>('steps');
  const [enabled, setEnabled] = useState(true);
  const [editing, setEditing] = useState(false);
  const [steps, setSteps] = useState<WorkflowStep[]>(INITIAL_STEPS);
  const [draft, setDraft] = useState<WorkflowStep[]>(INITIAL_STEPS);
  const [inputs, setInputs] = useState<WorkflowInput[]>(INITIAL_INPUTS);
  const [inputDraft, setInputDraft] = useState<WorkflowInput[]>(INITIAL_INPUTS);
  const [runs, setRuns] = useState<WorkflowRun[]>(INITIAL_RUNS);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(INITIAL_RUNS[0]?.id ?? null);
  const [liveRunId, setLiveRunId] = useState<string | null>(null);
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const visibleSteps = editing ? draft : steps;
  const visibleInputs = editing ? inputDraft : inputs;
  const running = liveRunId !== null;
  function startEditing() {
    setDraft(steps);
    setInputDraft(inputs);
    setEditing(true);
    setTab('steps');
  }
  function saveEditing() {
    setSteps(draft);
    setInputs(inputDraft);
    setEditing(false);
  }
  function changeStep(id: string, patch: Partial<WorkflowStep>) {
    setDraft(prev => prev.map(s => s.id === id ? {
      ...s,
      ...patch
    } : s));
  }
  function removeStep(id: string) {
    setDraft(prev => prev.filter(s => s.id !== id));
  }
  function addStep() {
    setDraft(prev => [...prev, {
      id: `step-${Date.now()}`,
      title: 'Describe what this step should do',
      body: 'Write the instruction in plain language — Giga will pick the right tool and credential at run time.'
    }]);
  }
  function changeInput(id: string, patch: Partial<WorkflowInput>) {
    setInputDraft(prev => prev.map(i => i.id === id ? {
      ...i,
      ...patch
    } : i));
  }
  function removeInput(id: string) {
    setInputDraft(prev => prev.filter(i => i.id !== id));
  }
  function addInput() {
    setInputDraft(prev => [...prev, {
      id: `in-${Date.now()}`,
      key: `input_${prev.length + 1}`,
      label: 'New input',
      type: 'Text',
      required: true,
      hint: 'Explain what this value is for.'
    }]);
  }
  /** Streams a fresh run: each step goes running -> success on a timer. */
  function runNow() {
    if (running) return;
    const id = `run-${Date.now()}`;
    const pending: StepTrace[] = steps.map(s => ({
      stepId: s.id,
      status: 'pending',
      duration: '—',
      detail: 'Waiting on the previous step…',
      log: []
    }));
    const run: WorkflowRun = {
      id,
      trigger: 'Manual',
      actor: 'Tony Ennis',
      startedAt: 'Just now',
      relative: 'just now',
      duration: '—',
      status: 'running',
      inputValues: inputs.reduce<Record<string, string>>((acc, input) => {
        acc[input.key] = DEFAULT_INPUT_VALUES[input.key] ?? '';
        return acc;
      }, {}),
      credits: 0,
      traces: pending
    };
    setRuns(prev => [run, ...prev]);
    setSelectedRunId(id);
    setLiveRunId(id);
    setTab('runs');
    setEditing(false);
    const patch = (fn: (r: WorkflowRun) => WorkflowRun) => setRuns(prev => prev.map(r => r.id === id ? fn(r) : r));
    let elapsed = 400;
    steps.forEach((step, i) => {
      timers.current.push(window.setTimeout(() => {
        patch(r => ({
          ...r,
          traces: r.traces.map((t, ti) => ti === i ? {
            ...t,
            status: 'running',
            detail: LIVE_STEP_DETAIL[i] ?? 'Working…'
          } : t)
        }));
      }, elapsed));
      elapsed += 1400;
      timers.current.push(window.setTimeout(() => {
        patch(r => ({
          ...r,
          credits: r.credits + 4,
          traces: r.traces.map((t, ti) => ti === i ? {
            ...t,
            status: 'success',
            duration: LIVE_STEP_DURATION[i] ?? '2.0s',
            detail: (LIVE_STEP_LOG[i] ?? [])[LIVE_STEP_LOG[i]?.length - 1] ?? 'Completed',
            log: LIVE_STEP_LOG[i] ?? []
          } : t)
        }));
      }, elapsed));
      elapsed += 300;
    });
    timers.current.push(window.setTimeout(() => {
      patch(r => ({
        ...r,
        status: 'success',
        duration: '32.7s',
        output: OUTPUT_URL
      }));
      setLiveRunId(null);
    }, elapsed));
  }
  return <WorkflowAppShell data-id="element-12490" data-mp="162fhb7srcv1n">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8 pb-28" data-id="element-12491" data-mp="1a1xz3upa8q09">
        {/* Breadcrumb */}
        <a href="#" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-12492" data-mp="1f1anijvsy54y">
          <ArrowLeft size={14} data-id="element-12493" data-mp="1jyj7mq0igj8u" />
          Workflows
        </a>

        {/* Header */}
        <header className="mt-4 rounded-3xl border border-[#E7E3DD] bg-white p-5 md:p-6" data-id="element-12494" data-mp="1os5axrksbarz">
          <div className="flex items-start gap-4" data-id="element-12495" data-mp="1tovp29d13war">
            <div className="w-11 h-11 md:w-12 md:h-12 shrink-0 rounded-2xl bg-[#EEF2FF] border border-[#E0E7FF] flex items-center justify-center" data-id="element-12496" data-mp="1trq93zs4rf3n">
              <ListChecks size={20} className="text-[#4F46E5]" data-id="element-12497" data-mp="22rn38gxah663" />
            </div>

            <div className="flex-1 min-w-0" data-id="element-12498" data-mp="2nu9utteich5">
              <div className="flex items-center gap-2.5 flex-wrap" data-id="element-12499" data-mp="2olw3e13yxur">
                <h1 className="font-title text-[22px] md:text-[27px] font-bold leading-tight" data-id="element-12500" data-mp="3ut697mtstzub">
                  {WORKFLOW.name}
                </h1>
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-[#E7E3DD] bg-[#F6F5F3] text-neutral-500'}`} data-id="element-12501" data-mp="cg05qemrxe1m">
                  <span className={`w-1.5 h-1.5 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-neutral-400'}`} aria-hidden="true" data-id="element-12502" data-mp="he1oszk79dd3" />
                  {enabled ? 'Active' : 'Paused'}
                </span>
              </div>
              <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-[#5C544C]" data-id="element-12503" data-mp="qega3yx5ysgl">
                {WORKFLOW.description}
              </p>
              <p className="mt-2.5 font-mono text-[11.5px] text-neutral-500" data-id="element-12504" data-mp="2s5bal42bfftn">
                {WORKFLOW.owner} · {WORKFLOW.updated} · {WORKFLOW.totalRuns} runs
              </p>
            </div>

            {/* Actions live at the top so Run now is never buried. */}
            <div className="hidden md:flex items-center gap-2 shrink-0" data-id="element-12505" data-mp="1clkfbhez5bmy">
              <ActionButtons enabled={enabled} setEnabled={setEnabled} editing={editing} running={running} onEdit={startEditing} onRun={runNow} data-id="element-12506" data-mp="yu5rmxa4gm9k" />
            </div>
          </div>

          <div className="mt-4 flex md:hidden items-center gap-2" data-id="element-12507" data-mp="owhw454ozaqc">
            <ActionButtons enabled={enabled} setEnabled={setEnabled} editing={editing} running={running} onEdit={startEditing} onRun={runNow} data-id="element-12508" data-mp="tvui6nnqodrv" />
          </div>
        </header>

        {/* Tabs */}
        <div className="mt-6 border-b border-[#E7E3DD]" role="tablist" aria-label="Workflow views" data-id="element-12509" data-mp="jxoi8blhthpn">
          <div className="flex items-center gap-1" data-id="element-12510" data-mp="2aa46xivw84j8">
            {[{
            id: 'steps' as Tab,
            label: 'Steps',
            icon: ListChecks,
            count: visibleSteps.length
          }, {
            id: 'runs' as Tab,
            label: 'Runs',
            icon: History,
            count: runs.length
          }].map(t => {
            const active = tab === t.id;
            return <button key={t.id} type="button" role="tab" aria-selected={active} onClick={() => setTab(t.id)} className={`relative inline-flex items-center gap-2 px-3.5 py-2.5 text-[14px] font-semibold transition-colors ${active ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-12511" data-mp="scj552flt3ej">
                  <t.icon size={15} data-id="element-12512" data-mp="ecrkiiul0ork" />
                  {t.label}
                  <span className={`rounded-full px-1.5 py-0.5 font-mono text-[10.5px] ${active ? 'bg-[#2B211A] text-white' : 'bg-[#F6F5F3] text-neutral-500'}`} data-id="element-12513" data-mp="eb6tv4be1pyz">
                    {t.count}
                  </span>
                  {t.id === 'runs' && running && <Loader2 size={12} className="text-[#EA580C] animate-spin" data-id="element-12514" data-mp="23rg0eu2dsouc" />}
                  {active && <motion.span layoutId="wf-tab" className="absolute inset-x-0 -bottom-px h-0.5 bg-[#2B211A]" data-id="element-12515" data-mp="1ysuvwig3jj03" />}
                </button>;
          })}
          </div>
        </div>

        {/* Panels */}
        <div className="mt-6" data-id="element-12516" data-mp="2bayz08o3yqo1">
          <AnimatePresence mode="wait" data-id="element-12517" data-mp="2ba5pizxrln27">
            <motion.div key={tab} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.2
          }} role="tabpanel" data-id="element-12518" data-mp="2b94bcmlgckg9">
              {tab === 'steps' ? <WorkflowStepsTab steps={visibleSteps} inputs={visibleInputs} editing={editing} runs={runs} onChangeStep={changeStep} onRemoveStep={removeStep} onAddStep={addStep} onChangeInput={changeInput} onRemoveInput={removeInput} onAddInput={addInput} onSave={saveEditing} onCancel={() => setEditing(false)} data-id="element-12519" data-mp="1sccyauwi71kn" /> : <WorkflowRunsTab runs={runs} steps={steps} inputs={inputs} selectedRunId={selectedRunId} onSelectRun={setSelectedRunId} data-id="element-12520" data-mp="38ql77mocpcdt" />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </WorkflowAppShell>;
}
function ActionButtons({
  enabled,
  setEnabled,
  editing,
  running,
  onEdit,
  onRun
}: {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  editing: boolean;
  running: boolean;
  onEdit: () => void;
  onRun: () => void;
}) {
  return <>
      <button type="button" onClick={() => setEnabled(!enabled)} className="inline-flex items-center gap-1.5 rounded-xl border border-[#E7E3DD] px-3 py-2 text-[13px] font-semibold text-[#5C544C] hover:bg-[#F6F5F3] transition-colors" aria-pressed={enabled} data-id="element-12521" data-mp="33g2be6ogj7tn">
        <Power size={14} data-id="element-12522" data-mp="2fowudo6s69hr" />
        {enabled ? 'Pause' : 'Activate'}
      </button>
      <button type="button" onClick={onEdit} disabled={editing} className="inline-flex items-center gap-1.5 rounded-xl border border-[#E7E3DD] px-3 py-2 text-[13px] font-semibold text-[#5C544C] hover:bg-[#F6F5F3] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-12523" data-mp="2jonre9mwhluq">
        <Pencil size={14} data-id="element-12524" data-mp="3y1j5lhtsec4" />
        {editing ? 'Editing' : 'Edit'}
      </button>
      <button type="button" onClick={onRun} disabled={running} className="inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#EA580C] disabled:opacity-70 disabled:cursor-wait transition-colors" data-id="element-12525" data-mp="e2hx29bqvlda">
        {running ? <>
            <Loader2 size={14} className="animate-spin" data-id="element-12526" data-mp="4abwib7ffvmh" />
            Running…
          </> : <>
            <Play size={14} data-id="element-12527" data-mp="9a70m3baxxp" />
            Run now
          </>}
      </button>
      <button type="button" className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-xl border border-[#E7E3DD] text-neutral-500 hover:bg-[#F6F5F3] transition-colors" aria-label="More options" data-id="element-12528" data-mp="3riz478qe7bo1">
        <MoreHorizontal size={16} data-id="element-12529" data-mp="3hlaxrljfcfq1" />
      </button>
    </>;
}